/**
 * sanitizeDocxHtml — sanitize mammoth's DOCX→HTML output before it is
 * rendered via dangerouslySetInnerHTML (DocxViewer).
 *
 * Approach: DOMParser walk with
 *   - a forbidden-tag list that removes script-capable and foreign-content
 *     elements entirely (svg/math carry their own attribute namespaces —
 *     xlink:href etc. — so they are dropped rather than attribute-scrubbed);
 *   - unconditional removal of every on* handler, style, and every
 *     URL-bearing attribute mammoth never emits (action/formaction/poster/
 *     srcset/…), so there is no attribute left to smuggle a URL through;
 *   - protocol allowlisting for the two attributes that legitimately carry
 *     URLs (href, src) AFTER normalizing away control/whitespace characters,
 *     because HTML attribute parsing ignores them ("ja\tvascript:" executes).
 *     data:image/* is allowed on src only — mammoth embeds pictures that way.
 */

const FORBIDDEN_TAGS = [
  'script', 'style', 'iframe', 'object', 'embed', 'link', 'meta', 'base',
  'form', 'svg', 'math', 'template',
];

// URL-carrying attributes that mammoth's output never needs — dropped
// outright instead of protocol-checked.
const DROP_ATTRS = [
  'action', 'formaction', 'xlink:href', 'poster', 'background', 'srcset',
  'cite', 'longdesc', 'usemap', 'data', 'dynsrc', 'lowsrc', 'ping',
];

// Control chars, whitespace, and NBSP — ignored by HTML URL parsing, so they
// must be stripped before the scheme is inspected.
const URL_JUNK_RE = new RegExp('[\\u0000-\\u0020\\u007f\\u00a0\\s]', 'g');

/**
 * True when a URL value is safe to keep: relative/fragment, or an
 * allowlisted scheme. Junk characters are stripped before the scheme check
 * so obfuscated schemes ("ja\tvascript:") can't slip through.
 */
export function isSafeUrl(value, { allowDataImage = false } = {}) {
  const cleaned = String(value).replace(URL_JUNK_RE, '').toLowerCase();
  if (!/^[a-z][a-z0-9+.-]*:/.test(cleaned)) return true; // relative or #fragment
  if (/^(https?:|mailto:|tel:)/.test(cleaned)) return true;
  if (allowDataImage && /^data:image\//.test(cleaned)) return true;
  return false;
}

export function sanitizeDocxHtml(dirty) {
  if (!dirty) return '';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(dirty, 'text/html');
    const walk = (node) => {
      if (node.nodeType === 1) { // Element node
        const tagName = node.tagName.toLowerCase();
        if (FORBIDDEN_TAGS.includes(tagName)) {
          node.remove();
          return;
        }
        Array.from(node.attributes).forEach((attr) => {
          const name = attr.name.toLowerCase();
          if (name.startsWith('on') || name === 'style' || DROP_ATTRS.includes(name)) {
            node.removeAttribute(attr.name);
          }
        });
        if (node.hasAttribute('href') && !isSafeUrl(node.getAttribute('href'))) {
          node.setAttribute('href', '#');
        }
        if (node.hasAttribute('src')
            && !isSafeUrl(node.getAttribute('src'), { allowDataImage: true })) {
          node.removeAttribute('src');
        }
      }
      Array.from(node.childNodes).forEach(walk);
    };
    walk(doc.body);
    return doc.body.innerHTML;
  } catch (e) {
    console.error('[sanitizeDocxHtml] sanitization failed:', e);
    return '';
  }
}
